// "Get the app": the Android app pays a one-time bonus once the app passes a Play Integrity check
// (lib/bonuses.ts on the API, Admin → Settings → Rewards). The website offers it on Android phones:
// a banner under the header (closable for two weeks) and a row on Rewards. Inside the app the same
// banner offers "Collect", which runs the check. Nothing shows on iPhones, on Rewards (it has its
// own row), or once the account has the bonus.
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useMe } from '../lib/session';
import { extrasQuery, statsQuery } from '../lib/queries';
import { isAppDevice } from '../lib/appDevice';
import { appCanVerify } from '../lib/notify';
import { collectAppBonus, type CollectResult } from '../lib/appBonus';
import { CoinGlyph, CoinMascot } from './Coin';
import { T, t, tc } from '../i18n';

export const PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.gaintplay.makemoney.android&referrer=utm_source%3Dwebsite';

const CLOSED = 'gp_getapp_closed';
const CLOSED_MS = 14 * 86_400_000;

/** An Android phone in a browser (not the app). */
export function isAndroidBrowser(): boolean {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent) && !isAppDevice();
}

/** The bonus this visitor can still get through the app, or 0. */
export function useAppBonusOffer(): number {
  const { data: me } = useMe();
  const { data: stats } = useQuery(statsQuery<{ appBonus?: number }>());
  const { data: extras } = useQuery({ ...extrasQuery(), enabled: !!me });
  if (me)
    return extras && !extras.appBonus.claimed && extras.appBonus.checkable
      ? extras.appBonus.coins
      : 0;
  return stats?.appBonus ?? 0;
}

/** "Collect": the app's Play Integrity check, then the coins (lib/appBonus.ts). */
export function CollectButton({
  coins,
  onDone,
  className = 'btn-primary pressable min-h-11 shrink-0 px-4 py-2 text-[15px]',
}: {
  coins: number;
  onDone: (r: CollectResult) => void;
  className?: string;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  // i18n: the app bonus's "Collect" button while the app checks the phone (a few seconds)
  const checking = t('Checking…');
  // i18n: button: collect the Android app bonus
  const collect = tc('action', 'Collect');
  return (
    <button
      type="button"
      aria-busy={busy || undefined}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          onDone(await collectAppBonus(coins));
        } catch (e) {
          onDone({
            ok: false,
            text: (e as Error).message || t('The check didn’t finish. Try again.'),
          });
        } finally {
          setBusy(false);
          await Promise.all(
            ['reward-extras', 'me', 'wallet'].map((k) => qc.invalidateQueries({ queryKey: [k] })),
          );
        }
      }}
      className={className}
    >
      {busy ? checking : collect}
    </button>
  );
}

/** The bonus in the banner's sentence: the amount with a coin, in coin colours. */
const amount = (c: ReactNode) => (
  <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-coin-ink">
    <CoinGlyph className="h-3.5 w-3.5" />
    {c}
  </span>
);

export function GetAppBanner() {
  const coins = useAppBonusOffer();
  const { data: me } = useMe();
  // Rewards has its own row for it.
  const onRewards = useLocation().pathname.startsWith('/rewards');
  const [closed, setClosed] = useState(() => {
    try {
      return Number(localStorage.getItem(CLOSED) ?? 0) > Date.now();
    } catch {
      return false;
    }
  });
  const [note, setNote] = useState<CollectResult | null>(null);
  // In the app (signed in, a version that can run the check): collect it here, once the email
  // is confirmed (the bonus waits for it; the verify banner below asks for that first).
  const inApp = typeof window !== 'undefined' && !!me && appCanVerify();
  if (inApp && !me.user.emailVerifiedAt) return null;
  if (closed || onRewards || (coins <= 0 && !note) || !(inApp || isAndroidBrowser())) return null;
  const close = () => {
    setClosed(true);
    try {
      localStorage.setItem(CLOSED, String(Date.now() + CLOSED_MS));
    } catch {
      /* closed until the next page load */
    }
  };
  return (
    <div className="mx-auto w-full max-w-[1200px] px-(--gutter) pt-4">
      <div className="flex items-center gap-3 rounded-[16px] border border-hairline bg-paper py-2 pl-2.5 pr-1.5">
        <CoinMascot size={36} rotate={-10} className="shrink-0" />
        {note ? (
          <p
            role="status"
            className={`min-w-0 flex-1 py-1 text-[14px] font-semibold leading-snug ${note.ok ? 'text-mint-deep' : 'text-graphite'}`}
          >
            {note.text}
          </p>
        ) : (
          <p className="min-w-0 flex-1 text-[14px] leading-snug text-graphite">
            <b className="block text-[15px] font-bold text-ink">
              {inApp ? t('Your app bonus is ready') : t('Get the Android app')}
            </b>
            {inApp ? (
              // i18n: under "Your app bonus is ready", in the Android app; <amount> is the bonus, with a coin icon
              <T
                msg="<amount>+{coins, plural, one {# coin} other {# coins}}</amount> for using the app."
                values={{ coins }}
                tags={{ amount }}
              />
            ) : (
              // i18n: under "Get the Android app": the bonus paid when you sign in to the app; <amount> is the bonus, with a coin icon
              <T
                msg="<amount>+{coins, plural, one {# coin} other {# coins}}</amount> when you sign in there."
                values={{ coins }}
                tags={{ amount }}
              />
            )}
          </p>
        )}
        {inApp ? (
          !note?.ok && (
            <CollectButton
              coins={coins}
              onDone={setNote}
              className="btn-primary pressable min-h-10 shrink-0 px-4 py-2 text-[14px]"
            />
          )
        ) : (
          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener"
            className="btn-primary pressable min-h-10 shrink-0 px-4 py-2 text-[14px]"
          >
            {
              // i18n: button: install the Android app from Google Play
              tc('action', 'Install')
            }
          </a>
        )}
        <button
          type="button"
          onClick={close}
          aria-label={t('Hide for now')}
          className="pressable grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-stone hover:bg-ink/5 hover:text-ink"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
